/**
 * The closed value sets the governance surface adds, built from
 * `@complan/domain`'s vocabulary exactly as `enums.ts` builds Foundation's.
 *
 * ## Why this is a separate module from `governance.ts`
 *
 * `filters.ts` needs two of these sets, for the Watchout severity and
 * Milestone_Readiness dimensions of `complan-governance` R6.11 and R6.12. But
 * `governance.ts` - the dashboard, roll-up, watchout and bulk-edit schemas - needs
 * `filters.ts`, because every governance request carries an `InitiativeFilter`.
 * Putting the enums in `governance.ts` would therefore close a cycle
 * `filters.ts -> governance.ts -> filters.ts`.
 *
 * Splitting the leaf value sets out breaks it without either module importing the
 * other's larger surface, and it mirrors what `enums.ts` already is: the leaf
 * vocabulary module every other schema file depends on.
 *
 * ## Two ways a set is kept honest to the domain
 *
 * The same two `enums.ts` uses. Where the domain exports the members as an array
 * ({@link WATCHOUT_SEVERITIES}, {@link READINESS_STATES}), `enumSchemaOf` builds
 * the schema straight from it and drift is impossible. Where the domain publishes
 * the union as a type only (`MetricState`, `MetricUnit`), `closedSet` rebuilds the
 * list under a `Record<TUnion, true>` annotation, so a missing member and an
 * invented one are both compile errors.
 */
import { z } from 'zod';
import { READINESS_STATES, WATCHOUT_SEVERITIES, } from '@complan/domain';
import { closedSet, enumSchemaOf } from './enums.js';
/**
 * The three Watchout severities of R7.1, straight from the domain array.
 *
 * Declaration order is `low`, `medium`, `high`, matching
 * `complan.watchout_severity`'s own declaration order so that PostgreSQL's
 * `order by severity desc` and the domain's `SEVERITY_RANK` agree without a
 * mapping (see `packages/domain/src/governance/watchout.ts`).
 */
export const watchoutSeveritySchema = enumSchemaOf(WATCHOUT_SEVERITIES);
/** The five Milestone_Readiness states of R8.3, straight from the domain array. */
export const readinessStateSchema = enumSchemaOf(READINESS_STATES);
/**
 * The three Milestone_Readiness states R6.12 makes filterable.
 *
 * A strict subset of {@link readinessStateSchema}, not the whole set:
 * `completed` and `planned` are the healthy states, and a filter for "has a
 * milestone that is fine" would select nearly the entire population, which is not
 * a governance question anyone asks. Stated as its own set rather than by omission
 * at the call site, so the restriction is visible where the dimension is defined.
 */
export const MILESTONE_READINESS_FILTER_STATES = Object.freeze([
    'overdue',
    'due',
    'missing',
]);
/** See {@link MILESTONE_READINESS_FILTER_STATES}. */
export const milestoneReadinessFilterSchema = enumSchemaOf(MILESTONE_READINESS_FILTER_STATES);
/**
 * The two Watchout_States of R7.2, checked exhaustively against the domain union.
 *
 * A *response* vocabulary only. No request schema accepts it: a client asking for
 * resolved watchouts sets `hasUnresolvedWatchout: false` on the filter instead,
 * which keeps one definition of open rather than two ways to ask for it.
 */
export const WATCHOUT_STATES = closedSet({ open: true, resolved: true });
/** See {@link WATCHOUT_STATES}. */
export const watchoutStateSchema = enumSchemaOf(WATCHOUT_STATES);
/**
 * The screens a Saved_View may belong to (R6.10, Assumption A12), matching
 * migration 27's `saved_view_screen_known` check exactly.
 *
 * `calendar` is first because it is the column's default and therefore the meaning
 * every Saved_View stored before migration 27 carries.
 */
export const SAVED_VIEW_SCREENS = Object.freeze([
    'calendar',
    'initiatives',
    'governance',
    'dashboard',
]);
/** See {@link SAVED_VIEW_SCREENS}. */
export const savedViewScreenSchema = enumSchemaOf(SAVED_VIEW_SCREENS);
/**
 * The three states a metric value can be in (R1.7, R5.10, R11.4), checked
 * exhaustively against the domain union.
 *
 * `undefined_empty_scope` is R1.7's: no readable record falls within the current
 * selection, so a share has a zero denominator and no value. It is deliberately
 * distinct from a computed zero, which is `defined` with `value: 0`.
 */
export const METRIC_STATES = closedSet({
    defined: true,
    undefined_empty_scope: true,
    data_incomplete: true,
});
/** See {@link METRIC_STATES}. */
export const metricStateSchema = enumSchemaOf(METRIC_STATES);
/** The three units a metric's number can carry (R2.1), checked against the domain union. */
export const METRIC_UNITS = closedSet({ count: true, percent: true, hours: true });
/** See {@link METRIC_UNITS}. */
export const metricUnitSchema = enumSchemaOf(METRIC_UNITS);
//# sourceMappingURL=governance-enums.js.map